import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdOutlineDeleteForever, MdOutlineGroupAdd, MdOutlineViewTimeline, MdSearch } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { ReactComponent as BluePenIcon } from "../../../assets/blue_pen_icon.svg";
import { ReactComponent as GreenCheckIcon } from "../../../assets/green_check_icon.svg";
import { ReactComponent as AssignIcon } from "../../../assets/Group453.svg";
import { ReactComponent as OpenBookIcon } from "../../../assets/open_book_icon.svg";
import { ReactComponent as RedCloseIcon } from "../../../assets/red_close_icon.svg";
import stars_icon from "../../../assets/stars_transparent.png";
import { ReactComponent as ClassroomReportsIcon } from "../../../assets/Subtraction3.svg";
import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AssignMultiStudentsModal from "../../../components/modals/common/AssignMultiStudentsModal";
import DeleteClassroomModal from "../../../components/modals/common/DeleteClassroomModal";
import EditClassroomModal from "../../../components/modals/common/EditClassroomModal";
import ClassroomTimelineModal from "../../../components/modals/school/ClassroomTimelineModal";
import ReassignModal from "../../../components/modals/school/ReassignModal";
import { USER_TYPE } from "../../../constants/common";
import {
  resetBreadcrumbs,
  setBreadcrumbDynamicID,
  setBreadcrumbDynamicPathNames,
} from "../../../redux/slices/breadcrumbsSlice";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const SchoolClassroomDetails = () => {
  const { id: classroomID } = useParams();
  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = getLanguage();

  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [classroomDetails, setClassroomDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [projectStats, setProjectStats] = useState(null);
  const [classroomTopStudents, setClassroomTopStudents] = useState([]);
  const [classroomStats, setClassroomStats] = useState("");
  const [schoolTopTenStudentsByClassroom, setSchoolTopTenStudentsByClassroom] = useState(null);

  const assignMultiStudentsModalRef = useRef();
  const deleteClassroomModalRef = useRef();
  const classroomTimelineModalRef = useRef();
  const reassignModalRef = useRef();
  const editModalRef = useRef();

  const openAssignMultiStudentsModal = (classroom) =>
    assignMultiStudentsModalRef.current.openModal(classroom);
  const openDeleteClassroomModal = (classroom) => deleteClassroomModalRef.current.openModal(classroom);
  const openClassroomTimelineModal = (classroom) => classroomTimelineModalRef.current.openModal(classroom);
  const openReassignModal = (classroom) => reassignModalRef.current.openModal(classroom);
  const openEditModal = (classroom) => editModalRef.current.openModal(classroom);

  const normalizeText = (text) => {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
  };

  const handleSearch = (event) => {
    const rawInput = event.target.value;
    setSearchText(rawInput);

    const normalizedQuery = normalizeText(rawInput);

    const filtered = students.filter((student) =>
      normalizeText(student.student.name).includes(normalizedQuery)
    );

    setFilteredStudents(filtered);
  };

  const columns = [
    {
      name: t("student"),
      field: "student.name", // Use field instead of selector
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.student.name}
          labelStyle="block w-44 overflow-hidden truncate whitespace-nowrap text-start"
          label={rowData.student.name}
          variant="link"
          color="blue"
          onClick={() => navigateStudentDetails(rowData.student.id)}
        />
      ),
    },
    {
      name: t("completed_lessons"),
      field: "completed_lessons", // Use field instead of selector
      sortable: true,
    },
    {
      name: t("total_points"),
      field: "student.total_points", // Use field instead of selector
      sortable: true,
    },
  ];

  const fetchClassroomTopStudents = async () => {
    try {
      const response = await classroomServices.getClassroomTopStudents(classroomID);

      setClassroomTopStudents(response);
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
    }
  };

  const getClassroomDetails = async () => {
    try {
      const response = await classroomServices.getClassroomDetails(classroomID);
      const response2 = await classroomServices.getClassroomStats(classroomID);

      setClassroomDetails(response);
      setClassroomStats(response2);
      dispatch(
        setBreadcrumbDynamicPathNames([response?.classroom?.teacher?.name, response?.classroom?.name])
      );
      dispatch(setBreadcrumbDynamicID([response?.classroom?.teacher?.id]));
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const getClassroomProjectStats = async () => {
    try {
      const res = await classroomServices.classroomProjectsStats(classroomID);
      setProjectStats(res);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };

  const fetchSchoolTopTenStudentsByClassroom = async () => {
    try {
      const response = await schoolServices.getSchoolTopTenStudentsByClassroom(classroomID);
      setSchoolTopTenStudentsByClassroom(response);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };

  // Fetch students based on selected classroom
  const getClassroomStudents = async () => {
    // setIsLoading(true);
    try {
      const res = await classroomServices.getStudentsByClassroomID(classroomID, {
        per_page: 10,
        order_dir: "asc",
      });

      setStudents(res.data);
      setFilteredStudents(res.data);
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    getClassroomDetails();
    getClassroomStudents();
    getClassroomProjectStats();
    fetchClassroomTopStudents();
    fetchSchoolTopTenStudentsByClassroom();
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      dispatch(resetBreadcrumbs());
    };
  }, []);

  const navigateStudentDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/students/${id}`);
  };
  const handleAssignStudents = () => {
    getClassroomStudents();
  };

  const handleDeleteClassroomSuccess = () => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/classrooms`);
  };

  const handleViewClassroomTimeline = () => {
    // Logic for viewing classroom timeline
    console.log("Viewing classroom timeline");
  };

  const handleReassignTeacher = () => {
    // Logic for reassigning teacher
    console.log("Teacher reassigned");
  };

  const handleEditClassroomSuccess = () => {
    getClassroomDetails();
  };

  const columns_2 = [
    {
      name: t("name"),
      field: "student_name",
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.student_name}
          labelStyle="block w-80 overflow-hidden truncate whitespace-nowrap text-start"
          label={rowData.student_name}
          variant="link"
          color="blue"
          onClick={() => navigateStudentDetails(rowData.student_id)}
        />
      ),
    },
    {
      name: t("points"),
      field: "total_points",
      sortable: true,
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-start lg:space-x-2">
        <Button
          iconLeft={<MdOutlineGroupAdd size={20} />}
          color="yellow"
          label={t("assign_students")}
          onClick={() => openAssignMultiStudentsModal(classroomDetails?.classroom)}
        />
        <Button
          iconLeft={<MdOutlineDeleteForever size={20} />}
          color="red"
          label={`${t("delete_classroom")}`}
          onClick={() => openDeleteClassroomModal(classroomDetails)}
        />
        <Button
          iconLeft={<MdOutlineViewTimeline size={20} />}
          label={`${t("classroom_timeline")}`}
          onClick={() => openClassroomTimelineModal(classroomDetails)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {/* Select Classroom, Challenges, and Playground Projects in One Row */}
        <div className="col-span-12 grid grid-cols-1 gap-4">
          {/* Display Selected Classroom */}
          <div className="col-span-12 flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface">
            <div>
              <div className="flex items-center">
                <h3 className="text-xl font-bold capitalize text-blue-700 dark:text-blue-400">
                  {classroomDetails?.classroom?.name} - {classroomDetails?.classroom?.section} - (
                  {classroomDetails?.grade?.name})
                </h3>
                <Button
                  tooltip={t("edit_classroom")}
                  icon={<FaEdit size={24} />}
                  variant="text"
                  onClick={() => openEditModal(classroomDetails)}
                />
              </div>
            </div>

            <Button
              tooltip={t("reassign_classroom")}
              variant="text"
              icon={<AssignIcon className={"text-[#ff602b]"} />}
              onClick={() => openReassignModal(classroomDetails)}
            />
          </div>
          <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
            {/* Classroom Report Section */}
            <div className="col-span-12 overflow-hidden rounded-xl bg-white shadow-lg dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
              {/* Dark overlay */}
              <img src={classroomDetails?.course?.image} alt="Course" className="h-36 w-full object-cover" />

              <div className="flex flex-col justify-end gap-1 bg-white p-4 dark:bg-dark-surface">
                {/* Bottom content section */}
                <h4 className="w-fit bg-[#842880] px-2 text-sm uppercase text-white dark:text-gray-300 rtl:w-20">
                  {t("course")}
                </h4>
                <h3 className="text-lg font-semibold text-[#842880] dark:text-white">
                  {classroomDetails?.course?.course}
                </h3>
              </div>
            </div>
            {/* Classroom Report Section */}
            <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
              <div className="flex flex-col items-center justify-center">
                <OpenBookIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("completed_lessons")}
                </h3>
              </div>
              <div>
                <p className="text-center text-[43px] font-semibold text-blue-500">
                  {classroomStats?.completed_lessons_count}
                </p>
                <span className="text-sm text-blue-500">
                  {t("out_of")} {classroomStats?.lesson_count}
                </span>
              </div>
            </div>

            {/* Total Course Challenges / Projects */}
            <div
              className={`col-span-12 flex flex-col justify-end rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 ${classroomDetails?.course?.for_children ? "xl:col-span-6" : "xl:col-span-3"}`}
            >
              <div className="px-3">
                <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                  {classroomDetails?.course?.for_children
                    ? t("total_course_project")
                    : t("total_course_challenge")}
                </h3>
                <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                  {classroomDetails?.course?.for_children
                    ? (projectStats?.approved_challenges || 0) +
                      (projectStats?.pending_challenges || 0) +
                      (projectStats?.rejected_challenges || 0)
                    : (projectStats?.approved_challenges || 0) +
                      (projectStats?.pending_challenges || 0) +
                      (projectStats?.rejected_challenges || 0)}
                </p>
                <div className="my-4">
                  <p className="mb-2 flex font-medium text-blue-600">
                    <span className="flex flex-1 items-center gap-2">
                      <BluePenIcon />
                      {t("pending")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.pending_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-green-500">
                    <span className="flex-2 flex items-center gap-2">
                      <GreenCheckIcon />
                      {t("approved")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.approved_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-red-500">
                    <span className="flex flex-1 items-center gap-2">
                      <RedCloseIcon />
                      {t("rejected")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.rejected_challenges || 0}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Playground Projects (only for non-children courses) */}
            {!classroomDetails?.course?.for_children && (
              <div className="col-span-12 flex flex-col justify-end rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-3">
                <div className="px-3">
                  <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                    {t("playground_projects")}
                  </h3>
                  <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                    {(projectStats?.approved_playground || 0) +
                      (projectStats?.pending_playground || 0) +
                      (projectStats?.rejected_playground || 0)}
                  </p>
                  <div className="my-4">
                    <p className="mb-2 flex font-medium text-blue-600">
                      <span className="flex flex-1 items-center gap-2">
                        <BluePenIcon />
                        {t("pending")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.pending_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-green-500">
                      <span className="flex-2 flex items-center gap-2">
                        <GreenCheckIcon />
                        {t("approved")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.approved_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-red-500">
                      <span className="flex flex-1 items-center gap-2">
                        <RedCloseIcon />
                        {t("rejected")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.rejected_playground || 0}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Classroom Report Section */}
            <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
              <div className="flex flex-1 flex-col items-center justify-center px-4">
                <ClassroomReportsIcon className="mb-4 h-14 w-14 text-blue-500" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("classroom_report")}
                </h3>
              </div>
              <Button
                variant="outlined"
                label={t("go_to_reports")}
                onClick={() =>
                  navigate(`/${USER_TYPE.SCHOOL.ROLE}/classroom-reports?teacherId=${1}&classroomId=${2}`)
                }
              />
            </div>
          </div>
        </div>
        <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface lg:col-span-12 xl:col-span-10">
            <CustomDataTable
              header={false}
              search={<h3 className="text-xl font-semibold text-green-500">{t("top_five_students")}</h3>}
              columns={columns_2}
              data={classroomTopStudents}
              withPagination={false}
              withSort
            />
          </div>
          <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
            <img src={stars_icon} alt="icon" />

            {schoolTopTenStudentsByClassroom?.length > 0 ? (
              schoolTopTenStudentsByClassroom?.slice(0, 2).map((student, index) => (
                <div
                  onClick={() => navigateStudentDetails(student?.id)}
                  key={student?.id || index}
                  className="group mb-2 w-full max-w-xs cursor-pointer rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-5 shadow-lg transition hover:scale-[1.02] dark:from-dark-surface dark:to-dark-surface/80"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="relative mb-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-3xl font-bold text-blue-600 shadow-inner ring-4 ring-blue-300 dark:bg-gray-700 dark:ring-blue-500">
                        {student?.name?.charAt(0)}
                      </div>
                      <span className="absolute -bottom-2 right-0 rounded-full bg-yellow-400 px-1 py-1 text-xs font-semibold text-white shadow-md">
                        ⭐
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-blue-700 group-hover:text-blue-900 dark:text-blue-300">
                      {student?.name}
                    </h3>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-4">
                <p className="mb-4 text-center text-xs text-mutedText dark:text-dark-mutedText">
                  {t("no_students_from_classroom")}
                </p>
              </div>
            )}

            <Button
              className="mt-2 w-full"
              variant="outlined"
              label={t("check_top_10_students")}
              onClick={() => navigate(`/${USER_TYPE.SCHOOL.ROLE}/top-10-students`)}
            />
          </div>
        </div>
        <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface">
          <CustomDataTable
            loading={loading}
            columns={columns}
            data={filteredStudents}
            withPagination
            withSort
            actions={
              <p className="text-right text-sm font-semibold rtl:text-left">
                <span className="text-slate-800 dark:text-white">{t("current_students")}: </span>
                <span className="text-blue-400"> {classroomDetails?.student_count}</span>
              </p>
            }
            search={
              <div className="relative w-1/3">
                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-auto rtl:right-3" />
                <input
                  type="text"
                  placeholder={t("search")}
                  value={searchText}
                  onChange={handleSearch}
                  className="w-full rounded-md border bg-white py-3 pl-10 pr-3 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white rtl:pl-3 rtl:pr-10"
                />
              </div>
            }
          />
        </div>
      </div>

      {/* Modals */}
      <AssignMultiStudentsModal ref={assignMultiStudentsModalRef} onSubmit={handleAssignStudents} />
      <DeleteClassroomModal ref={deleteClassroomModalRef} onSubmit={handleDeleteClassroomSuccess} />
      <ClassroomTimelineModal ref={classroomTimelineModalRef} onSubmit={handleViewClassroomTimeline} />
      <ReassignModal ref={reassignModalRef} onSubmit={handleReassignTeacher} />
      <EditClassroomModal ref={editModalRef} onSubmit={handleEditClassroomSuccess} />
    </>
  );
};

export default SchoolClassroomDetails;
